<template>
  <main class="page">
    <p v-if="loading" class="state">Chargement…</p>
    <p v-else-if="error" class="state error">{{ error }}</p>

    <template v-else-if="animal">
      <!-- TOP -->
      <section class="top">
        <div class="breadcrumb">
          <RouterLink to="/animaux">Animaux</RouterLink>
          <span>›</span>
          <span>{{ animal.name }}</span>
        </div>

        <div class="head">
          <div class="titleCol">
            <h1 class="title">{{ animal.name }}</h1>
            <p class="subtitle">
              {{ labelType(animal.type) }}
              <span class="sep">•</span>
              {{ ageLabel(animal.naissance) }}
              <span class="sep">•</span>
              {{ sexLabel(animal.sexe) }}
              <span class="sep">•</span>
              {{ animal.ville || "—" }}
            </p>

            <div class="chips">
              <span class="pill">{{ labelType(animal.type) }}</span>
              <span v-if="animal.race" class="pill">{{ animal.race }}</span>
              <span v-if="animal.couleur" class="pill">{{ animal.couleur }}</span>
              <span v-if="animal.sos" class="pill warn">SOS</span>
              <span v-if="animal.specific_needs" class="pill warn">Besoins spécifiques</span>
            </div>
          </div>

        </div>
      </section>

      <!-- GRID -->
      <section class="grid">
        <!-- LEFT -->
        <div class="left">
          <!-- Gallery -->
          <section class="card galleryCard">
            <div class="mainPhoto" :style="photoStyle2(selectedPhoto)"></div>

            <div class="thumbs" v-if="photos.length">
              <button
                v-for="(p, i) in photos"
                :key="i"
                class="thumb"
                :class="{ active: p === selectedPhoto }"
                type="button"
                :style="photoStyle(p)"
                @click="selectedPhoto = p"
              />
            </div>
          </section>

          <!-- Description -->
          <section class="card">
            <h2>À propos</h2>
            <div class="wysiwyg" v-html="animal.description || '<p>Aucune description pour le moment.</p>'"></div>
          </section>

          <!-- Infos -->
          <section class="card">
            <h2>Informations</h2>

            <div class="infoGrid">
              <div class="infoItem">
                <div class="label">Arrivé(e) le</div>
                <div class="value">{{ formatDate(animal.arrive) }}</div>
              </div>

              <div class="infoItem">
                <div class="label">Naissance</div>
                <div class="value">{{ formatDate(animal.naissance) }}</div>
              </div>

              <div class="infoItem">
                <div class="label">Stérilisé(e)</div>
                <div class="value">{{ boolLabel(animal.sterilise) }}</div>
              </div>

              <div class="infoItem">
                <div class="label">Identifié(e)</div>
                <div class="value">{{ boolLabel(animal.identifie) }}</div>
              </div>

              <div class="infoItem">
                <div class="label">Vacciné(e)</div>
                <div class="value">{{ boolLabel(animal.vaccin) }}</div>
              </div>

              <div class="infoItem">
                <div class="label">FIV</div>
                <div class="value">{{ triStateLabel(animal.fiv) }}</div>
              </div>
            </div>
          </section>

          <!-- Compat -->
          <section class="card">
            <h2>Compatibilités</h2>

            <div class="compat">
              <div class="compatItem">
                <div class="k">Enfants</div>
                <div class="v">{{ prettyEntente(animal.entente_enfant) }}</div>
              </div>
              <div class="compatItem">
                <div class="k">Chiens</div>
                <div class="v">{{ prettyEntente(animal.entente_chien) }}</div>
              </div>
              <div class="compatItem">
                <div class="k">Chats</div>
                <div class="v">{{ prettyEntente(animal.entente_chat) }}</div>
              </div>
            </div>
          </section>
        </div>

        <!-- RIGHT -->
        <aside class="right">
          <section class="card sticky">
            <div class="miniTitle">Résumé</div>

            <div class="summaryRow">
              <span class="dot"></span>
              <div>
                <div class="summaryKey">Caractère</div>
                <div class="summaryVal">{{ animal.caractere || "—" }}</div>
              </div>
            </div>

            <div class="summaryRow">
              <span class="dot"></span>
              <div>
                <div class="summaryKey">Refuge</div>
                <div class="summaryVal">Refuge #{{ animal.name ?? "—" }}</div>
              </div>
            </div>

            <div class="summaryRow">
              <span class="dot"></span>
              <div>
                <div class="summaryKey">Localisation</div>
                <div class="summaryVal">{{ animal.ville || "—" }}</div>
              </div>
            </div>

            <div class="ctaBox">
              <div class="ctaTitle">Intéressé(e) par {{ animal.name }} ?</div>
              <p class="ctaText">
                Contactez le refuge pour organiser une rencontre.
              </p>

              <div class="ctaRow">
                <a class="btn primary" :href="animal.website_url" target="_blank" rel="noopener noreferrer">Contacter</a>
                <RouterLink class="btn ghost" to="/refuges">Voir les refuges</RouterLink>
              </div>
            </div>

            <p class="note">
              Dernière mise à jour : <strong>{{ formatDateTime(animal.updated_at) }}</strong>
            </p>
          </section>
        </aside>
      </section>
    </template>

    <p v-else class="state error">Animal introuvable.</p>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();

const API_URL = "https://admin.asso-refuge.fr/getAllAnimal";
const IMG_BASE_URL = "https://admin.asso-refuge.fr/images/";

const loading = ref(true);
const error = ref(null);
const animals = ref([]);


const animalId = computed(() => Number(route.params.id));

const animal = computed(() => animals.value.find(a => Number(a.id) === animalId.value) || null);

const photos = computed(() => {
  const a = animal.value;
  if (!a) return [];
  return [a.img1, a.img2, a.img3, a.img4, a.img5]
    .filter(Boolean)
    .map(f => `${IMG_BASE_URL}${f}`);
});

const selectedPhoto = ref(null);

onMounted(async () => {
  try {
    loading.value = true;
    error.value = null;

    const res = await fetch(API_URL, { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error(`HTTP ${res.status} - ${res.statusText}`);

    const data = await res.json();
    animals.value = Array.isArray(data) ? data : (data?.data ?? []);

    selectedPhoto.value = photos.value[0] ?? null;
  } catch (e) {
    error.value = e?.message ?? String(e);
  } finally {
    loading.value = false;
  }
});



function photoStyle(url) {
  if (!url) return { background: "#e5e7eb" };
  return {
    backgroundImage: `url("${url}")`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  };
}

function photoStyle2(url) {
  if (!url) return { background: "#e5e7eb" };
  return {
    backgroundImage: `url("${url}")`,
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  };
}


function labelType(type) {
  if (!type) return "Animal";
  const t = String(type).toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function sexLabel(sexe) {
  const s = String(sexe ?? "").toLowerCase();
  if (s === "male" || s === "m") return "Mâle";
  if (s === "female" || s === "f") return "Femelle";
  return "—";
}

function ageLabel(naissance) {
  if (!naissance) return "Âge —";
  const birth = new Date(naissance);
  if (Number.isNaN(birth.getTime())) return "Âge —";

  const now = new Date();
  let months = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
  if (now.getDate() < birth.getDate()) months -= 1;
  months = Math.max(0, months);

  if (months >= 12) {
    const y = Math.floor(months / 12);
    return `${y} an${y > 1 ? "s" : ""}`;
  }
  return `${months} mois`;
}

function formatDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "2-digit" });
}

function formatDateTime(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleString("fr-FR", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function boolLabel(v) {
  if (v === 1 || v === true || v === "1" || v === "true") return "Oui";
  if (v === 0 || v === false || v === "0" || v === "false") return "Non";
  return "—";
}

function triStateLabel(v) {
  if (v === null || v === undefined || v === "") return "—";
  return boolLabel(v);
}

function prettyEntente(v) {
  if (!v) return "—";
  const s = String(v).toLowerCase();
  if (s === "inconnu") return "Inconnu";
  if (s === "oui") return "Ok";
  if (s === "non") return "Non";
  return v;
}
</script>

<style src="../assets/css/animauxdetails.css" scoped></style>
